import { PlaceOfServiceType } from '../../api';
import { formatDate } from '../../services/primitives/date-utils';
import { rosterFacilityCellText, rosterProviderCellText } from '../../services/roster/roster.columns';
import type { RosterFacility, RosterProvider, RosterRecord, RosterTab } from '../../services/roster/roster.types';
import { formatPlaceOfService } from '../claim-details/claim-overview/claim-overview.utils';

/**
 * What a Roster cell SHOWS — the same text the table renders and the CSV writes, so the file
 * matches the screen.
 *
 * On top of `roster*CellText` (which stays raw so it can sort): `updated` reads as a date, and a
 * place of service reads as its CMS code and label (`11 · Office`) rather than the enum. An empty
 * string still means "no value"; the table decides how absence looks.
 */
export function rosterDisplayText(tab: RosterTab, record: RosterRecord, key: string): string {
    if (key === 'updated') {
        return record.updated ? formatDate(record.updated, 'MMM d yyyy') : '';
    }
    if (tab === 'facilities') {
        const facility = record as RosterFacility;
        if (key === 'placeOfService') {
            return formatPlaceOfService(facility.placeOfServiceCode) || '';
        }
        return rosterFacilityCellText(facility, key);
    }
    return rosterProviderCellText(record as RosterProvider, key);
}

/**
 * Places of service for the facility drawer, labelled as the table shows them (`11 · Office`) and in
 * CMS code order. `UNKNOWN` is left out — it is what a record says when nobody chose, not a choice.
 */
export const ROSTER_PLACE_OF_SERVICE_OPTIONS: { value: PlaceOfServiceType; label: string }[] = Object.values(
    PlaceOfServiceType,
)
    .filter(value => value !== PlaceOfServiceType.UNKNOWN)
    .map(value => ({ value, label: formatPlaceOfService(value) || value }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
